import { createElement, isMobile } from './utils';
import { disableTimezoneSelection, enableTimezoneSelection } from './timezones';
export function fetchAvailability(instance, expertId, rootEl) {
    var _a, _b;
    const expert = instance.experts.find(e => e.id == expertId); // using loose equality as ID might be string/int
    const expertState = instance.stateManager.getExpertState(expertId);
    if (!expertState.date || !expert)
        return;
    const slotsContainer = (rootEl ? rootEl.querySelector(`#booking-slots-${expertId}`) : null) || document.getElementById(`booking-slots-${expertId}`);
    if (!slotsContainer)
        return;
    const slotsGrid = slotsContainer.querySelector('.slots-grid');
    slotsContainer.style.display = 'block';
    slotsGrid.innerHTML = `<div class="loading">${((_a = window.cgBookACallI18n) === null || _a === void 0 ? void 0 : _a.checkingAvailability) || 'Checking availability...'}</div>`;
    const dateStr = expertState.date;
    // Abort previous in-flight request and guard against stale responses
    try {
        (_b = expertState._slotsController) === null || _b === void 0 ? void 0 : _b.abort();
    }
    catch (_) { }
    const controller = new AbortController();
    expertState._slotsController = controller;
    const reqId = (expertState._slotsReqId || 0) + 1;
    expertState._slotsReqId = reqId;
    fetch(`/wp-json/book-a-call/v1/availability?expert_id=${expertId}&email=${encodeURIComponent(expert.email)}&date=${dateStr}&timezone=${expertState.timezone}`, { signal: controller.signal })
        .then(res => {
        if (!res.ok) {
            if (res.status === 409) { // conflict
                throw Object.assign(new Error('Slot conflict'), { code: 409 });
            }
            throw new Error(`API returned ${res.status}`);
        }
        return res.json();
    })
        .then(slots => {
        if (reqId !== expertState._slotsReqId)
            return; // stale response
        renderSlots(instance, expertId, slots, rootEl);
    })
        .catch((err) => {
        var _a, _b, _c;
        if ((err === null || err === void 0 ? void 0 : err.name) === 'AbortError')
            return;
        console.error('Failed to fetch availability:', err);
        const msg = err && err.code === 409
            ? (((_a = window.cgBookACallI18n) === null || _a === void 0 ? void 0 : _a.slotConflict) || 'That time was just booked by someone else. Please pick another time.')
            : (((_b = window.cgBookACallI18n) === null || _b === void 0 ? void 0 : _b.couldNotLoadTimes) || 'Could not load times.');
        const detailedMsg = ((err === null || err === void 0 ? void 0 : err.message) || '').includes('404')
            ? (((_c = window.cgBookACallI18n) === null || _c === void 0 ? void 0 : _c.backendRequired) || 'Book a Call Drawer plugin is required for booking functionality.')
            : msg;
        slotsGrid.innerHTML = `<div class="error" style="padding: 16px; text-align: center; color: #d32f2f; border: 1px solid #ffcdd2; border-radius: 4px; background: #ffebee;">${detailedMsg}</div>`;
    });
}
export function renderSlots(instance, expertId, slots, rootEl) {
    var _a, _b, _c, _d, _e;
    const expertState = instance.stateManager.getExpertState(expertId);
    const slotsContainer = (rootEl ? rootEl.querySelector(`#booking-slots-${expertId}`) : null) || document.getElementById(`booking-slots-${expertId}`);
    const slotsGrid = slotsContainer.querySelector('.slots-grid');
    let slotsSelect = slotsContainer.querySelector('.slots-select');
    let selectWrapper = slotsContainer.querySelector(`#slots-select-wrapper-${expertId}`);
    const dateDisplay = slotsContainer.querySelector('.selected-date-display');
    if (expertState.date && dateDisplay) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const [year, month, day] = expertState.date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        dateDisplay.textContent = `${dayNames[dateObj.getDay()]} ${dateObj.getDate()}`;
        dateDisplay.style.display = 'block';
    }
    if (slots.length === 0) {
        slotsContainer.querySelectorAll('.no-available-slots').forEach(el => el.remove());
        slotsGrid.querySelectorAll('.no-available-slots').forEach(el => el.remove());
        if (isMobile()) {
            if (selectWrapper)
                selectWrapper.style.display = 'none';
            const msg = createElement('div', 'no-available-slots');
            msg.style.textAlign = 'center';
            msg.style.color = '#FF4019';
            msg.style.marginTop = '16px';
            msg.style.fontWeight = '500';
            msg.textContent = ((_a = window.cgBookACallI18n) === null || _a === void 0 ? void 0 : _a.noAvailableSlots) || 'No available slots for this date';
            slotsContainer.insertBefore(msg, slotsGrid);
            slotsGrid.innerHTML = '';
        }
        else {
            slotsGrid.innerHTML = `
                <div class="no-available-slots" style="text-align: center; color: #FF4019; margin-top: 16px; font-weight: 500;">
                    ${((_b = window.cgBookACallI18n) === null || _b === void 0 ? void 0 : _b.noAvailableSlots) || 'No available slots for this date'}
                </div>
            `;
        }
        return;
    }
    const now = new Date();
    const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    const selectedDateStr = expertState.date || null;
    let slotsHTML = '';
    let availableCount = 0;
    slots.forEach(slot => {
        var _a, _b;
        const isSelected = expertState.time === slot.time;
        let isAvailable = true;
        if (selectedDateStr === todayStr) {
            const slotDateTime = createSlotDateTime(slot.time, selectedDateStr);
            if (slotDateTime && now >= slotDateTime)
                isAvailable = false;
        }
        if (isAvailable)
            availableCount++;
        const classes = ['time-slot'];
        if (isSelected)
            classes.push('selected');
        if (!isAvailable)
            classes.push('unavailable');
        slotsHTML += `
            <div class="${classes.join(' ')}" 
                 data-time="${slot.time}"
                 data-expert-id="${expertId}"
                 role="option"
                 aria-selected="${isSelected ? 'true' : 'false'}"
                 aria-disabled="${!isAvailable ? 'true' : 'false'}"
                 tabindex="${isAvailable ? '0' : '-1'}"
                 ${!isAvailable ? 'data-unavailable="true"' : ''}
                 title="${!isAvailable ? (((_a = window.cgBookACallI18n) === null || _a === void 0 ? void 0 : _a.slotNotAvailable) || 'This time slot is not available') : (((_b = window.cgBookACallI18n) === null || _b === void 0 ? void 0 : _b.availableForBooking) || 'Available for booking')}">
                ${slot.display}
                ${!isAvailable ? '<span class="unavailable-indicator" aria-hidden="true">✗</span>' : ''}
            </div>
        `;
    });
    if (!slotsSelect) {
        slotsSelect = createElement('select', 'slots-select', {
            id: `slots-select-${expertId}`,
            'data-expert-id': expertId
        });
        selectWrapper = createElement('div', 'slots-select-wrapper', { id: `slots-select-wrapper-${expertId}` });
        selectWrapper.appendChild(slotsSelect);
        slotsContainer.insertBefore(selectWrapper, slotsGrid);
    }
    else if (!selectWrapper) {
        selectWrapper = createElement('div', 'slots-select-wrapper', { id: `slots-select-wrapper-${expertId}` });
        slotsContainer.insertBefore(selectWrapper, slotsGrid);
        selectWrapper.appendChild(slotsSelect);
    }
    if (slotsSelect && !slotsSelect.dataset.expertId) {
        slotsSelect.dataset.expertId = expertId;
    }
    slotsSelect.innerHTML = `<option value="">${((_c = window.cgBookACallI18n) === null || _c === void 0 ? void 0 : _c.selectATime) || 'Select a time'}</option>`;
    if (availableCount === 0) {
        slotsContainer.querySelectorAll('.no-available-slots').forEach(el => el.remove());
        slotsGrid.querySelectorAll('.no-available-slots').forEach(el => el.remove());
        if (isMobile()) {
            if (selectWrapper)
                selectWrapper.style.display = 'none';
            const msg = createElement('div', 'no-available-slots');
            msg.style.textAlign = 'center';
            msg.style.color = '#FF4019';
            msg.style.marginTop = '16px';
            msg.style.fontWeight = '500';
            msg.textContent = ((_d = window.cgBookACallI18n) === null || _d === void 0 ? void 0 : _d.noAvailableSlots) || 'No available slots for this date';
            slotsContainer.insertBefore(msg, slotsGrid);
            slotsGrid.innerHTML = '';
        }
        else {
            slotsGrid.innerHTML = `
                ${slotsHTML}
                <div class="no-available-slots" style="text-align: center; color: #FF4019; margin-top: 16px; font-weight: 500;">
                    ${((_e = window.cgBookACallI18n) === null || _e === void 0 ? void 0 : _e.noAvailableSlots) || 'No available slots for this date'}
                </div>
            `;
        }
    }
    else {
        const existingMsg = slotsContainer.querySelector('.no-available-slots');
        if (existingMsg)
            existingMsg.remove();
        if (selectWrapper)
            selectWrapper.style.display = 'block';
        slotsGrid.innerHTML = slotsHTML;
        slots.forEach(slot => {
            const slotDateTime = createSlotDateTime(slot.time, selectedDateStr);
            const isAvailable = !(selectedDateStr === todayStr && slotDateTime && now >= slotDateTime);
            if (isAvailable) {
                const opt = createElement('option', '', { value: slot.time }, slot.display);
                slotsSelect.appendChild(opt);
            }
        });
    }
    if (availableCount > 0) {
        if (isMobile()) {
            if (slotsSelect)
                slotsSelect.focus();
        }
        else {
            const firstSlot = slotsGrid.querySelector('.time-slot:not(.unavailable)');
            if (firstSlot)
                firstSlot.focus();
        }
    }
}
export function createSlotDateTime(timeValue, dateStr) {
    try {
        const timeMatch = timeValue.match(/(\d{1,2}):(\d{2})/);
        if (!timeMatch)
            return null;
        const hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const datetime = new Date(dateStr + 'T00:00:00');
        datetime.setHours(hours, minutes, 0, 0);
        return datetime;
    }
    catch (_a) {
        return null;
    }
}
export function updateSelectedBookingInfo(instance, expertId, rootEl) {
    const expertState = instance.stateManager.getExpertState(expertId);
    const expert = instance.experts.find(e => e.id == expertId);
    const bookingInfoContainer = (rootEl ? rootEl.querySelector(`#selected-booking-info-${expertId}`) : null) || document.getElementById(`selected-booking-info-${expertId}`);
    if (!bookingInfoContainer || !expertState.date || !expertState.time)
        return;
    const [year, month, day] = expertState.date.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = dateObj.toLocaleDateString('en-US', options);
    const duration = expert.meeting_duration || 30;
    const [hours, minutes] = expertState.time.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + duration * 60000);
    const timeFormat = { hour: 'numeric', minute: '2-digit', hour12: true };
    const startTime = startDate.toLocaleTimeString('en-US', timeFormat);
    const endTime = endDate.toLocaleTimeString('en-US', timeFormat);
    bookingInfoContainer.innerHTML = `
        <div class="detail-item"><i class="ti ti-calendar"></i><span>${dateStr}</span></div>
        <div class="detail-item"><i class="ti ti-clock"></i><span>${startTime} - ${endTime}</span></div>
    `;
    bookingInfoContainer.style.display = 'block';
}
export function showFormView(instance, expertId, rootEl) {
    const calendarSlotsWrapper = (rootEl ? rootEl.querySelector(`#calendar-slots-wrapper-${expertId}`) : null) || document.getElementById(`calendar-slots-wrapper-${expertId}`);
    const formWrapper = (rootEl ? rootEl.querySelector(`#form-wrapper-${expertId}`) : null) || document.getElementById(`form-wrapper-${expertId}`);
    const backBtn = (rootEl ? rootEl.querySelector(`#back-btn-form-${expertId}`) : null) || document.getElementById(`back-btn-form-${expertId}`);
    const expertState = instance.stateManager.getExpertState(expertId);
    const expert = instance.experts.find(e => e.id == expertId);
    const timezoneSelect = (rootEl ? rootEl.querySelector(`#booking-timezone-${expertId}`) : null) || document.getElementById(`booking-timezone-${expertId}`);
    const timezoneWrapper = (rootEl ? rootEl.querySelector(`.timezone-select-wrapper[data-expert-id="${expertId}"]`) : null) || document.querySelector(`.timezone-select-wrapper[data-expert-id="${expertId}"]`);
    if (calendarSlotsWrapper)
        calendarSlotsWrapper.style.display = 'none';
    if (formWrapper)
        formWrapper.style.display = 'block';
    disableTimezoneSelection(timezoneSelect, timezoneWrapper);
    if (formWrapper && expertState.date && expertState.time && expert) {
        const form = formWrapper.querySelector('form');
        if (form) {
            const selectedTimezone = expertState.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            form.setAttribute('data-expert-email', expert.email || '');
            form.setAttribute('data-timezone', selectedTimezone);
            const [year, month, day] = expertState.date.split('-').map(Number);
            const [hours, minutes] = expertState.time.split(':').map(Number);
            const localDateTimeStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
            const offsetFormatter = new Intl.DateTimeFormat('en-US', { timeZone: selectedTimezone, timeZoneName: 'longOffset' });
            const refDate = new Date(`${localDateTimeStr}Z`);
            const parts = offsetFormatter.formatToParts(refDate);
            const offsetPart = parts.find(part => part.type === 'timeZoneName');
            let offsetMinutes = 0;
            if (offsetPart && offsetPart.value) {
                const m = offsetPart.value.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
                if (m) {
                    const sign = m[1] === '+' ? 1 : -1;
                    const oh = parseInt(m[2]);
                    const om = m[3] ? parseInt(m[3]) : 0;
                    offsetMinutes = sign * (oh * 60 + om);
                }
            }
            const startTimeUTC = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
            startTimeUTC.setUTCMinutes(startTimeUTC.getUTCMinutes() - offsetMinutes);
            let meetingDuration = parseInt(expert.meeting_duration || 30, 10);
            const endTimeUTC = new Date(startTimeUTC.getTime() + meetingDuration * 60 * 1000);
            form.setAttribute('data-start-time', startTimeUTC.toISOString());
            form.setAttribute('data-end-time', endTimeUTC.toISOString());
            form.setAttribute('data-expert-id', expertId);
            form.setAttribute('data-date', expertState.date);
            form.setAttribute('data-time', expertState.time);
        }
    }
    if (backBtn && !backBtn.dataset.bound) {
        backBtn.addEventListener('click', () => { showCalendarView(instance, expertId, rootEl); });
        backBtn.dataset.bound = 'true';
    }
}
export function showCalendarView(instance, expertId, rootEl) {
    const calendarSlotsWrapper = (rootEl ? rootEl.querySelector(`#calendar-slots-wrapper-${expertId}`) : null) || document.getElementById(`calendar-slots-wrapper-${expertId}`);
    const formWrapper = (rootEl ? rootEl.querySelector(`#form-wrapper-${expertId}`) : null) || document.getElementById(`form-wrapper-${expertId}`);
    const bookingInfoContainer = (rootEl ? rootEl.querySelector(`#selected-booking-info-${expertId}`) : null) || document.getElementById(`selected-booking-info-${expertId}`);
    const timezoneSelect = (rootEl ? rootEl.querySelector(`#booking-timezone-${expertId}`) : null) || document.getElementById(`booking-timezone-${expertId}`);
    const timezoneWrapper = (rootEl ? rootEl.querySelector(`.timezone-select-wrapper[data-expert-id="${expertId}"]`) : null) || document.querySelector(`.timezone-select-wrapper[data-expert-id="${expertId}"]`);
    if (calendarSlotsWrapper)
        calendarSlotsWrapper.style.display = '';
    if (formWrapper)
        formWrapper.style.display = 'none';
    if (bookingInfoContainer)
        bookingInfoContainer.style.display = 'none';
    enableTimezoneSelection(timezoneSelect, timezoneWrapper);
    instance.stateManager.getExpertState(expertId).time = null;
    if (window.restoreBookingHeight) {
        window.restoreBookingHeight(expertId);
    }
}
